import { ref, readonly } from 'vue';
import { useStorage } from '@/composables/use-storage';
import { getRecoverySessionData, saveRecoverySessionData } from '@/services/recoverySessionService';
import Socket from '@/services/websocket';
import { EVAL_TYPES } from '@/pages/eval/eval-builder/constants/evalTypes';

let socketInstance = null;

const evalSessionId = ref(null);
const evalPreviewData = ref(null);
const evalStatus = ref('idle'); // idle, creating, updating, completed, error
const evalError = ref(null);
const evalMessages = ref([]);
const isLoading = ref(false);

// Debounce timer for field updates
let updateDebounceTimer = null;
const UPDATE_DEBOUNCE_DELAY = 2000; // 2 seconds

// Local storage keys
const EVAL_MESSAGES_STORAGE_KEY = 'eval_messages';
const EVAL_PREVIEW_DATA_STORAGE_KEY = 'eval_preview_data';
const PREVIEW_EVAL_PAYLOAD_STORAGE_KEY = 'preview_eval_payload';
const RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_UPDATE = 'recovery_session_data/:eval_builder_update';
const RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_CREATE = 'recovery_session_data/:eval_builder_create';
const RECOVERY_SESSION_CACHE_INTERVAL_MS = 5000;

export function useEvals() {
  const {
    getUserInfo,
  } = useStorage();
  
  const userInfo = getUserInfo();

  // Throttle recovery-session cache writes to avoid rapid requests.
  let lastRecoverySessionCacheSaveAt = 0;
  let pendingRecoverySessionCacheTimer = null;
  let pendingRecoverySessionCachePayload = null;
  let pendingRecoverySessionCacheKey = null;

  const getStoredPreviewPayloadString = () => {
    const stored = localStorage.getItem(PREVIEW_EVAL_PAYLOAD_STORAGE_KEY);
    return stored && stored.trim().length > 0 ? stored : null;
  };

  const buildPreviewPayload = (previewData) => {
    const safePreview = previewData || {};
    return {
      evalName: safePreview.evalName || '',
      owner: safePreview.owner || userInfo?.username,
      ownerId: safePreview.ownerId || userInfo?.id,
      evalType: safePreview.evalType || EVAL_TYPES.QA,
      tasks: safePreview.tasks || [],
      meta: {
        lastUpdated: new Date().toISOString(),
        version: safePreview.meta?.version || 1,
        recoverySession: safePreview.meta?.recoverySession || false
      }
    };
  };

  /**
   * Save eval messages to local storage
   */
  const saveEvalMessagesToStorage = (messages) => {
    try {
      const dataToStore = {
        messages,
        timestamp: Date.now(),
        userId: userInfo?.id
      };
      localStorage.setItem(EVAL_MESSAGES_STORAGE_KEY, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error saving eval messages to storage:', error);
    }
  };

  /**
   * Load eval messages from local storage
   */
  const loadEvalMessagesFromStorage = () => {
    try {
      const stored = localStorage.getItem(EVAL_MESSAGES_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.userId === userInfo?.id) {
          evalMessages.value = data.messages || [];
          return true;
        }
      }
    } catch (error) {
      console.error('Error loading eval messages from storage:', error);
    }
    return false;
  };

  /**
   * Save eval preview data to local storage
   */
  const scheduleRecoverySessionCacheSave = async (payload, key, forceCacheSave = false) => {
    try {
      if (!userInfo?.id) return;

      pendingRecoverySessionCachePayload = payload;
      pendingRecoverySessionCacheKey = key;

      const now = Date.now();
      const elapsed = now - lastRecoverySessionCacheSaveAt;
      const shouldSaveNow = forceCacheSave || elapsed >= RECOVERY_SESSION_CACHE_INTERVAL_MS;

      if (shouldSaveNow) {
        if (pendingRecoverySessionCacheTimer) {
          clearTimeout(pendingRecoverySessionCacheTimer);
          pendingRecoverySessionCacheTimer = null;
        }

        const payloadToSave = pendingRecoverySessionCachePayload;
        const keyToSave = pendingRecoverySessionCacheKey;
        const storedPayloadString = getStoredPreviewPayloadString();
        const valueToSave = storedPayloadString || JSON.stringify(payloadToSave);

        try {
          await saveRecoverySessionDataToCache({
            key: keyToSave,
            value: valueToSave,
            id: userInfo?.id
          });
          lastRecoverySessionCacheSaveAt = Date.now();
          pendingRecoverySessionCachePayload = null;
          pendingRecoverySessionCacheKey = null;
          return;
        } catch (error) {
          console.error('Error saving recovery session data (throttled):', error);
        }
      }

      if (pendingRecoverySessionCacheTimer) return;

      const delay = Math.max(RECOVERY_SESSION_CACHE_INTERVAL_MS - elapsed, 0);
      pendingRecoverySessionCacheTimer = setTimeout(async () => {
        try {
          pendingRecoverySessionCacheTimer = null;
          if (!pendingRecoverySessionCachePayload) return;

          const payloadToSave = pendingRecoverySessionCachePayload;
          const keyToSave = pendingRecoverySessionCacheKey;
          const storedPayloadString = getStoredPreviewPayloadString();
          const valueToSave = storedPayloadString || JSON.stringify(payloadToSave);

          await saveRecoverySessionDataToCache({
            key: keyToSave,
            value: valueToSave,
            id: userInfo?.id
          });
          lastRecoverySessionCacheSaveAt = Date.now();
          pendingRecoverySessionCachePayload = null;
          pendingRecoverySessionCacheKey = null;
        } catch (error) {
          console.error('Error saving recovery session data (throttled):', error);
        }
      }, delay);
    } catch (error) {
      console.error('Error scheduling recovery session cache save:', error);
    }
  };

  const saveEvalPreviewDataToStorage = async (
    previewData,
    cacheKey = null,
    { forceCacheSave = false, skipCacheWrite = false } = {}
  ) => {
    try {
      const payload = buildPreviewPayload(previewData);
      const hadExistingPayload = !!localStorage.getItem(PREVIEW_EVAL_PAYLOAD_STORAGE_KEY);
      localStorage.setItem(PREVIEW_EVAL_PAYLOAD_STORAGE_KEY, JSON.stringify(payload));
      const key = cacheKey || (hadExistingPayload ? RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_UPDATE : RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_CREATE);
      if (!skipCacheWrite) {
        await scheduleRecoverySessionCacheSave(payload, key, forceCacheSave);
      }
    } catch (error) {
      console.error('Error saving eval preview data to storage:', error);
    }
  };

  /**
   * Load eval preview data from local storage
   */
  const loadEvalPreviewDataFromStorage = () => {
    try {
      const stored = localStorage.getItem(EVAL_PREVIEW_DATA_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.userId === userInfo?.id) {
          evalPreviewData.value = data.previewData || null;
          return true;
        }
      }
    } catch (error) {
      console.error('Error loading eval preview data from storage:', error);
    }
    return false;
  };

  /**
   * Save recovery session data to local storage
   */
  const saveRecoverySessionDataToCache = async (data) => {
    try {
      if (data) {
        await saveRecoverySessionData({
          key: data.key,
          value: data.value,
          id: data.id || userInfo?.id
        });
      } else {
        console.error('No data to save to recovery session');
      }
    } catch (error) {
      console.error('Error saving recovery session data to storage:', error);
    }
  };

  /**
   * Load recovery session data from local storage
   * Returns the JSON stringified data or null if not found
   * Checks both create and update keys
   */
  const loadRecoverySessionDataFromCache = async (preferredKey = null) => {
    try {
      const keysToTry = preferredKey 
        ? [preferredKey]
        : [RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_UPDATE, RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_CREATE];
      
      const normalizeRecoverySessionData = (rawData) => {
        if (rawData == null) return null;
        if (typeof rawData === 'string') {
          try {
            return JSON.parse(rawData);
          } catch (error) {
            console.error('Error parsing recovery session data:', error);
            return null;
          }
        }
        return rawData;
      };

      for (const key of keysToTry) {
        const data = await getRecoverySessionData({
          key: key,
          id: userInfo?.id
        });
        if (!data) {
          const stored = localStorage.getItem(key);
          if (stored) {
            return JSON.parse(stored);
          }
        }
        if (data) {
          const normalizedData = normalizeRecoverySessionData(data);
          if (!normalizedData) continue;
          evalPreviewData.value = normalizedData;
          await saveEvalPreviewDataToStorage(normalizedData, key, { skipCacheWrite: true });
          return JSON.stringify(normalizedData);
        }
      }
    } catch (error) {
      console.error('Error loading recovery session data from storage:', error);
    }
    return null;
  };

  /**
   * Handle eval message event from WebSocket
   */
  const handleEvalMessage = async (data) => {
    if (data) {
      // Add message to array
      evalMessages.value.push({
        ...data,
        timestamp: Date.now()
      });
      
      // Save to local storage
      saveEvalMessagesToStorage(evalMessages.value);
      
      // Update status if provided
      if (data.status) {
        evalStatus.value = data.status;
      }
      
      // Update preview data if provided
      if (data.preview_data) {
        evalPreviewData.value = data.preview_data;
        await saveEvalPreviewDataToStorage(data.preview_data, RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_CREATE);
      }
      
      // Handle errors
      if (data.error) {
        evalError.value = data.error;
        evalStatus.value = 'error';
      }
    }
  };

  /**
   * Handle eval preview created event from WebSocket
   */
  const handleEvalPreviewCreated = async (data) => {
    if (data) {
      evalPreviewData.value = data;
      evalStatus.value = 'created';
      evalSessionId.value = data.session_id || data.eval_session_id;
      
      // Save to local storage and server cache
      await saveEvalPreviewDataToStorage(data, RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_CREATE);
    }
  };

  /**
   * Handle eval preview status update event from WebSocket
   */
  const handleEvalPreviewStatusUpdated = async (data) => {
    if (data) {
      evalStatus.value = data.status;
      
      if (data.error) {
        evalError.value = data.error;
        evalStatus.value = 'error';
      }
      
      if (data.preview_data) {
        evalPreviewData.value = data.preview_data;
        await saveEvalPreviewDataToStorage(data.preview_data, RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_UPDATE);
      }
      
      // Emit acknowledgment back to backend
      if (socketInstance) {
        const ackPayload = {
          session_id: evalSessionId.value,
          user_id: userInfo?.id,
          status: 'acknowledged',
          received_status: data.status,
          timestamp: Date.now()
        };
        
        socketInstance.sendEvalPreviewStatusUpdated(ackPayload);
      }
    }
  };

  /**
   * Clear preview eval payload from local storage
   */
  const clearPreviewEvalPayload = async () => {
    try {
      localStorage.removeItem(PREVIEW_EVAL_PAYLOAD_STORAGE_KEY);
      await saveEvalPreviewDataToStorage({
        evalName: '',
        owner: '',
        ownerId: '',
        tasks: []
      }, RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_CREATE);
      await saveEvalPreviewDataToStorage({
        evalName: '',
        owner: '',
        ownerId: '',
        tasks: []
      }, RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_UPDATE);
    } catch (error) {
      console.error(`Error clearing ${PREVIEW_EVAL_PAYLOAD_STORAGE_KEY} from localStorage:`, error);
    }
  };

  /**
   * Initialize WebSocket connection for evals
   */
  const initializeWebSocket = (sessionId) => {
    if (!userInfo) {
      console.warn('Cannot initialize WebSocket: user not logged in');
      return null;
    }

    if (!socketInstance) {
      const handlers = {
        handleEvalMessage,
        handleEvalPreviewCreated,
        handleEvalPreviewStatusUpdated,
      };

      socketInstance = new Socket(handlers);
    }

    evalSessionId.value = sessionId || `eval_${userInfo?.id}_${Date.now()}`;

    socketInstance.connectEval(userInfo, evalSessionId.value);
    
    return socketInstance;
  };

  /**
   * Disconnect WebSocket
   */
  const disconnectWebSocket = () => {
    if (socketInstance) {
      socketInstance.disconnectEval();
      socketInstance = null;
    }
  };

  /**
   * Send eval preview creation request via WebSocket
   */
  const sendEvalPreviewCreated = async (evalData) => {
    try {
      if (!socketInstance) {
        console.error('WebSocket not initialized');
        return {
          success: false,
          error: 'WebSocket not initialized'
        };
      }

      isLoading.value = true;
      evalStatus.value = 'creating';
      evalError.value = null;

      const payload = {
        session_id: evalSessionId.value,
        user_id: userInfo?.id,
        eval_name: evalData.evalName,
        owner_user_id: evalData.selectedOwner,
        tasks: evalData.tasksJson,
        answers: evalData.answersJson,
        timestamp: Date.now(),
      };

      // Include references if available
      if (evalData.referencesZip) {
        payload.has_references = true;
      }

      socketInstance.sendEvalPreviewCreated(payload);

      return {
        success: true,
        session_id: evalSessionId.value
      };
    } catch (error) {
      console.error('Error sending eval preview created:', error);
      evalStatus.value = 'error';
      evalError.value = error.message;
      
      return {
        success: false,
        error: error.message
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Send eval preview status update via WebSocket (debounced)
   */
  const sendEvalPreviewStatusUpdate = (updateData, immediate = false) => {
    // Clear existing timer
    if (updateDebounceTimer) {
      clearTimeout(updateDebounceTimer);
    }

    const sendUpdate = () => {
      try {
        if (!socketInstance) {
          console.warn('WebSocket not initialized for status update');
          return;
        }

        evalStatus.value = 'updating';

        const payload = {
          session_id: evalSessionId.value,
          user_id: userInfo?.id,
          status: updateData?.status || 'updating',
          timestamp: Date.now(),
          field: updateData?.field || null,
          value: updateData?.value || null,
          ...updateData,
        };

        // Ensure payload is not empty
        if (!payload.session_id || !payload.user_id) {
          console.error('Invalid payload - missing session_id or user_id:', payload);
          return;
        }

        socketInstance.sendEvalPreviewStatusUpdated(payload);
      } catch (error) {
        console.error('Error sending eval preview status update:', error);
        evalError.value = error.message;
      }
    };

    if (immediate) {
      sendUpdate();
    } else {
      // Debounce the update
      updateDebounceTimer = setTimeout(sendUpdate, UPDATE_DEBOUNCE_DELAY);
    }
  };

  /**
   * Send field change updates (debounced by default)
   */
  const sendFieldUpdate = (fieldName, fieldValue, fieldStatus = 'field_updated', immediate = false) => {
    if (fieldStatus === 'eval_created') {
      evalStatus.value = 'creating';
    }

    const updateData = {
      status: fieldStatus,
      field: fieldName,
      value: fieldValue,
    };

    sendEvalPreviewStatusUpdate(updateData, immediate);
  };

  /**
   * Initialize eval builder with WebSocket
   */
  const initializeEvalBuilder = (sessionId = null) => {
    evalStatus.value = 'idle';
    evalError.value = null;
    evalPreviewData.value = null;
    
    // Load data from local storage
    loadEvalMessagesFromStorage();
    loadEvalPreviewDataFromStorage();
    
    initializeWebSocket(sessionId);
    
    const cleanup = () => {
      // Clear any pending debounce timers
      if (updateDebounceTimer) {
        clearTimeout(updateDebounceTimer);
        updateDebounceTimer = null;
      }
      
      disconnectWebSocket();
      
      // Reset state
      evalSessionId.value = null;
      evalPreviewData.value = null;
      evalStatus.value = 'idle';
      evalError.value = null;
      evalMessages.value = [];
    };

    return cleanup;
  };

  /**
   * Create eval with WebSocket integration
   */
  const createEvalWithWebSocket = async (evalData) => {
    try {
      // Initialize WebSocket if not already done
      if (!socketInstance) {
        initializeWebSocket();
      }

      // Send preview creation via WebSocket
      const result = await sendEvalPreviewCreated(evalData);
      
      return result;
    } catch (error) {
      console.error('Error creating eval with WebSocket:', error);
      evalStatus.value = 'error';
      evalError.value = error.message;
      
      return {
        success: false,
        error: error.message
      };
    }
  };

  /**
   * Update eval builder fields (with debounce)
   */
  const updateEvalBuilderField = (fieldName, fieldValue, fieldStatus, immediate = false) => {
    sendFieldUpdate(fieldName, fieldValue, fieldStatus, immediate);
  };

  const hasValidPreviewDataForAutosave = (previewData) => {
    const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;
    const hasEvalName = isNonEmptyString(previewData?.evalName);
    const hasTaskPromptAndAnswer = !!previewData?.tasks?.some((t) => (
      isNonEmptyString(t?.prompt) && isNonEmptyString(t?.answer)
    ));

    // Only flush when evalName, prompt, and answer are present.
    return hasEvalName && hasTaskPromptAndAnswer;
  };

  /**
   * Flush any pending updates immediately
   */
  const flushPendingUpdates = () => {
    if (updateDebounceTimer) {
      clearTimeout(updateDebounceTimer);
      updateDebounceTimer = null;
    }
  };

  /**
   * Flush eval builder recovery session to cache
   */
  const flushEvalBuilderRecoverySessionToCache = async () => {
    try {
      if (!hasValidPreviewDataForAutosave(evalPreviewData.value)) return;

      if (pendingRecoverySessionCacheTimer) {
        clearTimeout(pendingRecoverySessionCacheTimer);
        pendingRecoverySessionCacheTimer = null;
      }

      if (!userInfo?.id) return;

      const storedPayloadString = getStoredPreviewPayloadString();

      let valueToSave = storedPayloadString;
      if (!valueToSave) {
        const payload = buildPreviewPayload(evalPreviewData.value);
        valueToSave = JSON.stringify(payload);
        localStorage.setItem(PREVIEW_EVAL_PAYLOAD_STORAGE_KEY, valueToSave);
      }

      if (!valueToSave) return;

      await saveRecoverySessionDataToCache({
        key: RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_CREATE,
        value: valueToSave,
        id: userInfo?.id
      });
      await saveRecoverySessionDataToCache({
        key: RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_UPDATE,
        value: valueToSave,
        id: userInfo?.id
      });

      lastRecoverySessionCacheSaveAt = Date.now();
      pendingRecoverySessionCachePayload = null;
      pendingRecoverySessionCacheKey = null;
    } catch (error) {
      console.error('Error flushing eval builder recovery session to cache:', error);
    }
  };

  return {
    // Readonly state
    evalSessionId: readonly(evalSessionId),
    evalPreviewData: readonly(evalPreviewData),
    evalStatus: readonly(evalStatus),
    evalError: readonly(evalError),
    evalMessages: readonly(evalMessages),
    isLoading: readonly(isLoading),
    // Local storage keys
    PREVIEW_EVAL_PAYLOAD_STORAGE_KEY: readonly(PREVIEW_EVAL_PAYLOAD_STORAGE_KEY),
    EVAL_MESSAGES_STORAGE_KEY: readonly(EVAL_MESSAGES_STORAGE_KEY),
    EVAL_PREVIEW_DATA_STORAGE_KEY: readonly(EVAL_PREVIEW_DATA_STORAGE_KEY),
    // Recovery session data keys
    RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_UPDATE: readonly(RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_UPDATE),
    RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_CREATE: readonly(RECOVERY_SESSION_DATA_KEY_EVAL_BUILDER_CREATE),
    // Methods
    initializeEvalBuilder,
    createEvalWithWebSocket,
    updateEvalBuilderField,
    clearPreviewEvalPayload,
    sendEvalPreviewCreated,
    sendEvalPreviewStatusUpdate,
    sendFieldUpdate,
    flushPendingUpdates,
    disconnectWebSocket,
    handleEvalMessage,
    handleEvalPreviewCreated,
    handleEvalPreviewStatusUpdated,
    loadEvalMessagesFromStorage,
    loadEvalPreviewDataFromStorage,
    saveEvalMessagesToStorage,
    saveEvalPreviewDataToStorage,
    saveRecoverySessionDataToCache,
    loadRecoverySessionDataFromCache,
    flushEvalBuilderRecoverySessionToCache,
  };
}
